import { osnovaApi } from '../base';
import type { CeoBankAccountsSummary } from './ceo';

// =============================================================================
// Модели данных: Фабрика платежей (fin_payment_requests)
// =============================================================================

export interface FinPaymentRequestRow {
  id: string;
  request_number: string;
  source_module:
    | 'mto'
    | 'logist'
    | 'smeta'
    | 'chronum_payroll'
    | 'taxes_ens'
    | 'debt_leasing'
    | 'manual'
    | 'staff'
    | string;
  source_doc_ref: string;
  request_date: string;
  due_pay_date: string;
  project_id: string | null;
  project_name: string | null;
  contract_id: string | null;
  contract_number: string | null;
  recommended_account_id: string | null;
  recommended_account_name: string | null;
  recommended_account_number: string | null;
  ceo_approval_id: string | null;
  ceo_decision: string | null;
  counterparty_name: string;
  counterparty_inn: string | null;
  amount: number;
  vat_amount: number;
  category: string;
  priority: 'critical' | 'high' | 'normal' | 'low' | string;
  purpose: string;
  initiator_name: string | null;
  gate_contract_status: 'passed' | 'warning' | 'blocked' | string;
  gate_primary_doc_status: 'passed' | 'warning' | 'blocked' | string;
  gate_budget_status: 'within_limit' | 'within_tolerance' | 'over_limit' | 'unbudgeted' | string;
  gate_account_routing: 'settlement_ok' | 'obs_targeted' | 'tax_reserve_ok' | 'insufficient_funds' | string;
  gate_notes: string | null;
  status:
    | 'draft'
    | 'in_review'
    | 'ready_for_ceo'
    | 'on_ceo_approval'
    | 'approved'
    | 'deferred'
    | 'rejected'
    | 'exported_1c'
    | 'executed'
    | string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  registry_id: string | null;
  registry_number?: string | null;
  counterparty_id: string | null;
  counterparty_kpp: string | null;
  recipient_account_number: string | null;
  recipient_bik: string | null;
  recipient_bank_name: string | null;
  recipient_corr_account: string | null;
  requested_amount: number;
  approved_amount: number;
  cut_amount: number;
  carryover_request_id: string | null;
  carryover_from_request_id: string | null;
  paid_amount: number;
  payment_execution_status: 'unpaid' | 'partially_paid' | 'paid' | string;
  last_paid_date: string | null;
  bank_doc_numbers: string | null;
  external_1c_doc_id: string | null;
  /** Заявка сотрудника («Заявки на оплату», `source_module: 'staff'`), из которой пришла эта. */
  staff_request_id: string | null;
  /** Отдел сотрудника — для вопроса «с кого спросить». */
  staff_department: string | null;
  /** Причина решения Финансиста (отказ, отсрочка) — её видит сотрудник. */
  decision_comment: string | null;
}

export type PaymentRequestItem = FinPaymentRequestRow;

export interface PaymentRequestsFilter {
  status?: string;
  source_module?: string;
  project_id?: string;
  registry_id?: string;
  payment_execution_status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreatePaymentRequestInput {
  source_module: string;
  source_doc_ref?: string;
  request_date?: string;
  due_pay_date?: string;
  project_id?: string;
  contract_id?: string;
  recommended_account_id?: string;
  registry_id?: string;
  counterparty_id?: string;
  counterparty_name: string;
  counterparty_inn?: string;
  counterparty_kpp?: string;
  recipient_account_number?: string;
  recipient_bik?: string;
  recipient_bank_name?: string;
  recipient_corr_account?: string;
  amount: number;
  requested_amount?: number;
  vat_amount?: number;
  category?: string;
  priority?: string;
  purpose: string;
  initiator_name?: string;
}

export type CreatePaymentRequestPayload = CreatePaymentRequestInput;

export interface CollectRequestsResult {
  collected_count: number;
  requests: FinPaymentRequestRow[];
  breakdown_by_module: Record<string, number>;
}

export interface SubmitToCeoInput {
  request_ids: string[];
}

export interface SubmitToCeoResult {
  submitted_count: number;
  approval_ids: string[];
}

export interface Export1cInput {
  request_ids?: string[];
}

export interface Export1cResult {
  file_content: string;
  requests_count: number;
  total_amount: number;
  exported_ids: string[];
  /** Налоги ЕНС из выгружаемого реестра: в файл не попали, их проводят в банк-клиенте. */
  skipped_tax_requests?: string[];
}

export type Export1CResponse = Export1cResult;

// =============================================================================
// Модели данных: Реестры платежей (fin_payment_registries)
// =============================================================================

export interface FinPaymentRegistryRow {
  id: string;
  registry_number: string;
  registry_date: string;
  title: string;
  status: 'forming' | 'on_approval' | 'approved' | 'executing' | 'closed' | string;
  source_type: 'manual' | '1c_api' | '1c_document' | 'excel_import' | 'mixed' | string;
  total_requested_amount: number;
  total_approved_amount: number;
  total_cut_carryover_amount: number;
  total_rejected_amount: number;
  total_paid_amount: number;
  items_count: number;
  paid_items_count: number;
  notes: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  items?: FinPaymentRequestRow[];
}

export type PaymentRegistryItem = FinPaymentRegistryRow;

export interface FinPaymentRegistriesFilter {
  status?: string;
  from_date?: string;
  to_date?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreatePaymentRegistryInput {
  registry_number?: string;
  registry_date?: string;
  title: string;
  source_type?: string;
  notes?: string;
  auto_include_unassigned?: boolean;
}

export interface DecidePaymentRequestInput {
  decision: 'approved' | 'partially_approved' | 'deferred' | 'rejected' | 'pending' | string;
  approved_amount?: number;
  carryover_remainder?: boolean;
  next_pay_date?: string;
  comment?: string;
}

export interface DecidePaymentRequestResult {
  decided_request: FinPaymentRequestRow;
  carryover_request: FinPaymentRequestRow | null;
  affected_registry_id: string | null;
}

export interface MarkPaymentRequestPaidInput {
  paid_amount: number;
  payment_date?: string;
  bank_doc_number?: string;
  notes?: string;
}

// =============================================================================
// Модели данных: Справочник контрагентов из 1С (fin_counterparties)
// =============================================================================

export interface FinCounterpartyRow {
  id: string;
  name: string;
  full_name: string | null;
  inn: string;
  kpp: string | null;
  ogrn: string | null;
  counterparty_type: 'supplier' | 'subcontractor' | 'customer' | 'leasing' | 'bank' | 'tax_authority' | 'other' | string;
  account_number: string | null;
  bik: string | null;
  bank_name: string | null;
  corr_account: string | null;
  default_category: string;
  external_1c_guid: string | null;
  external_1c_code: string | null;
  source: '1c_api' | '1c_statement' | '1c_document' | 'excel' | 'contracts' | 'manual' | string;
  contracts_count: number;
  is_verified: boolean;
  notes: string | null;
  last_synced_at: string;
  created_at: string;
  updated_at: string;
}

export type CounterpartyItem = FinCounterpartyRow;

export interface CounterpartiesFilter {
  search?: string;
  counterparty_type?: string;
  source?: string;
  is_verified?: boolean;
  limit?: number;
  offset?: number;
}

export type FinCounterpartyFilter = CounterpartiesFilter;

export interface UpsertCounterpartyInput {
  name: string;
  full_name?: string;
  inn: string;
  kpp?: string;
  ogrn?: string;
  counterparty_type?: string;
  account_number?: string;
  bik?: string;
  bank_name?: string;
  corr_account?: string;
  default_category?: string;
  external_1c_guid?: string;
  external_1c_code?: string;
  source?: string;
  notes?: string;
}

export type CreateCounterpartyInput = UpsertCounterpartyInput;

// =============================================================================
// Модели данных: Интеграция с 1С, Импорт документов и Сверка выписок
// =============================================================================

export interface OneCSyncInput {
  registry_id?: string;
  force_buffer?: boolean;
}

export interface Sync1CResult {
  synced_counterparties: number;
  imported_requests: number;
  registry_id: string | null;
  connection_mode: string;
  message: string;
  synced_at: string;
}

export interface ImportDocumentInput {
  content: string;
  format?: 'auto' | '1c_client_bank' | 'json' | 'tsv' | 'csv_semicolon' | 'csv_comma' | string;
  registry_id?: string;
}

export interface ImportRegistryResult {
  created_counterparties_count: number;
  updated_counterparties_count: number;
  created_requests_count: number;
  registry_id: string;
  format_detected: string;
}

export interface ReconcileStatementsInput {
  statement_content?: string;
  registry_id?: string;
}

export interface ReconcileStatementsResult {
  matched_payments_count: number;
  total_matched_paid_amount: number;
  fully_paid_count: number;
  partially_paid_count: number;
  updated_request_ids: string[];
}

// =============================================================================
// Модели данных: Шлюз с ПК «Хронум» (fin_payroll_batches & site_accruals)
// =============================================================================

export interface FinPayrollBatchRow {
  id: string;
  chronum_batch_id: string;
  period_month: string;
  payout_date: string;
  batch_type: 'advance' | 'final_salary' | 'shift_allowance' | 'vacation' | 'dismissal' | 'bonus' | string;
  title: string;
  company_name: string;
  project_id: string | null;
  project_name: string;
  salary_bank_name: string;
  account_id: string | null;
  employees_count: number;
  shifts_verified_count: number;
  gross_accrued_amount: number;
  ndfl_amount: number;
  canteen_deduction_amount: number;
  other_deductions_amount: number;
  net_payout_amount: number;
  insurance_contrib_amount: number;
  field_discrepancy_pct: number;
  status: 'verified_chronum' | 'in_payment_queue' | 'on_ceo_approval' | 'approved' | 'paid' | string;
  payment_request_id: string | null;
  synced_at: string;
  notes: string | null;
}

export interface FinChronumSiteAccrualRow {
  id: string;
  period_month: string;
  chronum_site_id: string;
  chronum_site_name: string;
  company_name: string;
  project_id: string | null;
  project_name: string;
  workers_attached: number;
  on_shift_now: number;
  shifts_worked_mtd: number;
  field_reported_shifts_mtd: number;
  avg_shift_rate_rub: number;
  accrued_gross_fot_rub: number;
  accrued_ndfl_rub: number;
  accrued_insurance_rub: number;
  canteen_deduction_rub: number;
  total_labor_cost_rub: number;
  synced_at: string;
}

export interface ChronumPayrollTotals {
  total_attached_workers: number;
  on_shift_now: number;
  shifts_worked_mtd: number;
  gross_accrued_fot: number;
  ndfl_amount: number;
  insurance_contrib_amount: number;
  canteen_deductions: number;
  net_payout_amount: number;
  avg_shift_rate_rub: number;
}

export interface ChronumPayrollOverview {
  batches: FinPayrollBatchRow[];
  site_accruals: FinChronumSiteAccrualRow[];
  totals: ChronumPayrollTotals;
}

export type ChronumPayrollOverviewResponse = ChronumPayrollOverview;

export interface ChronumSyncResult {
  is_live_external: boolean;
  sites_updated_count: number;
  total_fot_rub: number;
  total_workers: number;
  synced_at: string;
}

// =============================================================================
// Модели данных: Авансы и удержания (fin_advance_retentions)
// =============================================================================

export interface FinAdvanceRetentionRow {
  id: string;
  direction: 'payable_advance' | 'receivable_advance' | 'receivable_retention' | 'payable_retention' | string;
  project_id: string | null;
  contract_id: string | null;
  project_name: string;
  counterparty_name: string;
  counterparty_inn: string | null;
  contract_number: string;
  initial_amount: number;
  settled_amount: number;
  remaining_amount: number;
  issue_date: string;
  expected_close_date: string | null;
  status: 'active' | 'partially_settled' | 'settled' | 'overdue' | 'claim_sent' | string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type AdvanceRetentionItem = FinAdvanceRetentionRow;

export interface CreateAdvanceRetentionInput {
  direction: string;
  project_id?: string;
  contract_id?: string;
  project_name?: string;
  counterparty_name: string;
  counterparty_inn?: string;
  contract_number?: string;
  initial_amount: number;
  settled_amount?: number;
  issue_date?: string;
  expected_close_date?: string;
  status?: string;
  notes?: string;
}

// =============================================================================
// Модели данных: Налоговый календарь ЕНС (fin_tax_calendar_items)
// =============================================================================

export interface FinTaxCalendarItemRow {
  id: string;
  tax_period: string;
  due_date: string;
  tax_type: 'vat_1_3' | 'ndfl' | 'insurance_contrib' | 'profit_tax' | 'property_tax' | 'ens_replenishment' | string;
  title: string;
  entity_name: string;
  accrued_amount: number;
  reserved_amount: number;
  paid_amount: number;
  reserve_account_id: string | null;
  reserve_account_name: string | null;
  status: 'planned' | 'reserved' | 'paid' | 'overdue' | string;
  calculation_basis: string | null;
  created_at: string;
  updated_at: string;
}

export type TaxCalendarItem = FinTaxCalendarItemRow;

export interface CreateTaxItemInput {
  tax_period: string;
  due_date: string;
  tax_type: string;
  title: string;
  entity_name?: string;
  accrued_amount: number;
  reserved_amount?: number;
  paid_amount?: number;
  reserve_account_id?: string;
  status?: string;
  calculation_basis?: string;
}

export interface FinTaxCalendarSummary {
  vat_outgoing_signed: number;
  vat_incoming_deductible: number;
  net_vat_payable: number;
  tax_reserve_balance: number;
  reserve_coverage_pct: number;
  items: FinTaxCalendarItemRow[];
}

// =============================================================================
// Модели данных: Сводный пульт CFO / Казначея (FinancierDashboardSummary)
// =============================================================================

export interface PaymentFactorySummary {
  total_requests_count: number;
  in_review_count: number;
  ready_for_ceo_count: number;
  on_ceo_approval_count: number;
  approved_ready_for_1c_count: number;
  total_in_review_amount: number;
  total_passed_gates_amount: number;
  total_over_budget_amount: number;
  total_on_ceo_approval_amount: number;
  total_approved_amount: number;
}

export interface WorkingCapitalSummary {
  issued_advances_unsettled: number;
  received_advances_balance: number;
  guarantee_retentions_receivable: number;
  guarantee_retentions_payable: number;
  overdue_receivables: number;
}

export interface TaxDashboardSummary {
  vat_outgoing_signed: number;
  vat_incoming_deductible: number;
  net_vat_payable: number;
  tax_reserve_balance: number;
  reserve_coverage_pct: number;
  upcoming_tax_payment_amount: number;
  next_tax_due_date: string | null;
}

/** Ковенант из карточки кредита или лизинга (ОСНОВА.Финансист → «Кредиты и лизинг»).
 *  Обороты и срок в карточке не ведутся — приходят пустыми. */
export interface BankCovenantItem {
  bank_name: string;
  covenant_name: string;
  required_turnover_month: number | null;
  actual_turnover_month: number | null;
  compliance_pct: number | null;
  status: 'ok' | 'warning' | 'breach' | string;
  deadline: string | null;
}

export interface CashFlowDayForecast {
  date: string;
  opening_balance: number;
  planned_inflows: number;
  planned_outflows: number;
  closing_balance: number;
  is_cash_gap: boolean;
}

export interface FinancierDashboardSummary {
  accounts_summary: CeoBankAccountsSummary;
  payment_factory: PaymentFactorySummary;
  chronum_payroll: ChronumPayrollTotals;
  working_capital: WorkingCapitalSummary;
  tax_summary: TaxDashboardSummary;
  bank_covenants: BankCovenantItem[];
  cash_flow_forecast_30d: CashFlowDayForecast[];
}

export type FinancierDashboardResponse = FinancierDashboardSummary;

// =============================================================================
// RTK Query Slice
// =============================================================================

export const financierApi = osnovaApi.injectEndpoints({
  endpoints: (build) => ({
    getFinancierDashboard: build.query<FinancierDashboardSummary, void>({
      query: () => '/agents/finance/dashboard',
      providesTags: ['FinancierDashboard'],
    }),

    listPaymentRequests: build.query<FinPaymentRequestRow[], PaymentRequestsFilter | void>({
      query: (filter) => ({
        url: '/agents/finance/payment-requests',
        params: filter ?? undefined,
      }),
      providesTags: ['FinancierRequest'],
    }),

    createPaymentRequest: build.mutation<FinPaymentRequestRow, CreatePaymentRequestInput>({
      query: (body) => ({
        url: '/agents/finance/payment-requests',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FinancierRequest', 'FinancierDashboard'],
    }),

    collectPaymentRequests: build.mutation<CollectRequestsResult, void>({
      query: () => ({
        url: '/agents/finance/payment-requests/collect',
        method: 'POST',
      }),
      invalidatesTags: [
        'FinancierRequest',
        'FinancierDashboard',
        'FinancierPayrollBatch',
        'FinancierTaxCalendar',
      ],
    }),

    // 'PayRequest' — здесь, в решении, отметке оплаты и сверке с выпиской: этап
    // «Заявки на оплату» сотрудника выводится из её заявки Фабрики.
    submitRequestsToCeo: build.mutation<SubmitToCeoResult, SubmitToCeoInput>({
      query: (body) => ({
        url: '/agents/finance/payment-requests/submit-to-ceo',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'FinancierRequest',
        'FinancierDashboard',
        'CeoApproval',
        'CeoDashboard',
        'PayRequest',
      ],
    }),

    exportApprovedTo1C: build.mutation<Export1cResult, Export1cInput | void>({
      query: (body) => ({
        url: '/agents/finance/payment-requests/export-1c',
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: [
        'FinancierRequest',
        'FinancierPayrollBatch',
        'FinancierDashboard',
        'CeoApproval',
        'CeoDashboard',
      ],
    }),

    getChronumPayrollOverview: build.query<ChronumPayrollOverview, { period_month?: string } | void>({
      query: (params) => ({
        url: '/agents/finance/chronum-payroll',
        params: params ?? undefined,
      }),
      providesTags: ['FinancierPayrollBatch', 'FinancierChronumSite'],
    }),

    syncChronumPayroll: build.mutation<ChronumSyncResult, void>({
      query: () => ({
        url: '/agents/finance/chronum-payroll/sync',
        method: 'POST',
      }),
      invalidatesTags: ['FinancierPayrollBatch', 'FinancierChronumSite', 'FinancierDashboard'],
    }),

    enqueuePayrollBatchToPayment: build.mutation<FinPaymentRequestRow, { batch_id: string }>({
      query: ({ batch_id }) => ({
        url: `/agents/finance/chronum-payroll/${batch_id}/enqueue`,
        method: 'POST',
      }),
      invalidatesTags: [
        'FinancierPayrollBatch',
        'FinancierRequest',
        'FinancierDashboard',
        'CeoApproval',
        'CeoDashboard',
      ],
    }),

    listAdvancesRetentions: build.query<
      FinAdvanceRetentionRow[],
      { direction?: string; project_id?: string; status?: string } | void
    >({
      query: (params) => ({
        url: '/agents/finance/advances-retentions',
        params: params ?? undefined,
      }),
      providesTags: ['FinancierAdvanceRetention'],
    }),

    createAdvanceRetention: build.mutation<FinAdvanceRetentionRow, CreateAdvanceRetentionInput>({
      query: (body) => ({
        url: '/agents/finance/advances-retentions',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FinancierAdvanceRetention', 'FinancierDashboard'],
    }),

    listTaxCalendar: build.query<FinTaxCalendarSummary, { tax_period?: string; status?: string } | void>({
      query: (params) => ({
        url: '/agents/finance/taxes-ens',
        params: params ?? undefined,
      }),
      providesTags: ['FinancierTaxCalendar'],
    }),

    createTaxItem: build.mutation<FinTaxCalendarItemRow, CreateTaxItemInput>({
      query: (body) => ({
        url: '/agents/finance/taxes-ens',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FinancierTaxCalendar', 'FinancierDashboard'],
    }),

    listPaymentRegistries: build.query<FinPaymentRegistryRow[], FinPaymentRegistriesFilter | void>({
      query: (params) => ({
        url: '/agents/finance/registries',
        params: params ?? undefined,
      }),
      providesTags: ['FinancierRegistry'],
    }),

    createPaymentRegistry: build.mutation<FinPaymentRegistryRow, CreatePaymentRegistryInput>({
      query: (body) => ({
        url: '/agents/finance/registries',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FinancierRegistry', 'FinancierRequest', 'FinancierDashboard'],
    }),

    getPaymentRegistry: build.query<FinPaymentRegistryRow, string>({
      query: (id) => `/agents/finance/registries/${id}`,
      providesTags: ['FinancierRegistry'],
    }),

    recalculatePaymentRegistry: build.mutation<FinPaymentRegistryRow, string>({
      query: (id) => ({
        url: `/agents/finance/registries/${id}/recalculate`,
        method: 'POST',
      }),
      invalidatesTags: ['FinancierRegistry', 'FinancierRequest', 'FinancierDashboard'],
    }),

    decidePaymentRequest: build.mutation<
      DecidePaymentRequestResult,
      { id: string } & DecidePaymentRequestInput
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/finance/requests/${id}/decide`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'FinancierRequest',
        'FinancierRegistry',
        'FinancierDashboard',
        'CeoApproval',
        'CeoDashboard',
        'PayRequest',
      ],
    }),

    markPaymentRequestPaid: build.mutation<
      FinPaymentRequestRow,
      { id: string } & MarkPaymentRequestPaidInput
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/finance/requests/${id}/mark-paid`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'FinancierRequest',
        'FinancierRegistry',
        'FinancierDashboard',
        'CeoApproval',
        'CeoDashboard',
        'PayRequest',
      ],
    }),

    listFinCounterparties: build.query<FinCounterpartyRow[], CounterpartiesFilter | void>({
      query: (params) => ({
        url: '/agents/finance/counterparties',
        params: params ?? undefined,
      }),
      providesTags: ['FinancierCounterparty'],
    }),

    createOrUpdateCounterparty: build.mutation<FinCounterpartyRow, UpsertCounterpartyInput>({
      query: (body) => ({
        url: '/agents/finance/counterparties',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FinancierCounterparty', 'FinancierRequest', 'FinancierDashboard'],
    }),

    sync1CApi: build.mutation<Sync1CResult, OneCSyncInput | void>({
      query: (body) => ({
        url: '/agents/finance/1c/sync',
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: [
        'FinancierCounterparty',
        'FinancierRequest',
        'FinancierRegistry',
        'FinancierDashboard',
      ],
    }),

    importRegistryDocument: build.mutation<ImportRegistryResult, ImportDocumentInput>({
      query: (body) => ({
        url: '/agents/finance/registries/import-document',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'FinancierCounterparty',
        'FinancierRequest',
        'FinancierRegistry',
        'FinancierDashboard',
      ],
    }),

    reconcileBankStatements: build.mutation<ReconcileStatementsResult, ReconcileStatementsInput | void>({
      query: (body) => ({
        url: '/agents/finance/registries/reconcile-statements',
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: [
        'FinancierRequest',
        'FinancierRegistry',
        'FinancierDashboard',
        'CeoApproval',
        'CeoDashboard',
        'PayRequest',
      ],
    }),
  }),
});

export const {
  useGetFinancierDashboardQuery,
  useListPaymentRequestsQuery,
  useCreatePaymentRequestMutation,
  useCollectPaymentRequestsMutation,
  useSubmitRequestsToCeoMutation,
  useExportApprovedTo1CMutation,
  useGetChronumPayrollOverviewQuery,
  useSyncChronumPayrollMutation,
  useEnqueuePayrollBatchToPaymentMutation,
  useListAdvancesRetentionsQuery,
  useCreateAdvanceRetentionMutation,
  useListTaxCalendarQuery,
  useCreateTaxItemMutation,
  useListPaymentRegistriesQuery,
  useCreatePaymentRegistryMutation,
  useGetPaymentRegistryQuery,
  useRecalculatePaymentRegistryMutation,
  useDecidePaymentRequestMutation,
  useMarkPaymentRequestPaidMutation,
  useListFinCounterpartiesQuery,
  useCreateOrUpdateCounterpartyMutation,
  useSync1CApiMutation,
  useImportRegistryDocumentMutation,
  useReconcileBankStatementsMutation,
} = financierApi;
